import { useState, useCallback, useRef, useEffect } from 'react';
import { getFileByName } from '../services/fileService';

const imageCache = new Map();

export const useImageCache = () => {
  const [, forceUpdate] = useState({});
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getCachedImage = useCallback((filename) => {
    return imageCache.get(filename);
  }, []);

  const fetchAndCacheImage = useCallback(async (filename) => {
    if (!filename) {
      throw new Error('No filename provided');
    }

    const cached = imageCache.get(filename);
    if (cached) {
      return cached;
    }

    try {
      const blob = await getFileByName(filename);
      const url = URL.createObjectURL(blob);
      
      const cacheEntry = {
        url,
        blob,
        filename,
        timestamp: Date.now()
      };
      
      imageCache.set(filename, cacheEntry);
      
      if (isMountedRef.current) {
        forceUpdate({});
      }
      
      return cacheEntry;
    } catch (error) {
      console.error('Failed to fetch and cache image:', filename, error);
      throw error;
    }
  }, []);

  const clearCache = useCallback(() => {
    imageCache.forEach(entry => {
      if (entry.url) {
        URL.revokeObjectURL(entry.url);
      }
    });
    imageCache.clear();
    forceUpdate({});
  }, []);

  const removeCachedImage = useCallback((filename) => {
    const cached = imageCache.get(filename);
    if (cached && cached.url) {
      URL.revokeObjectURL(cached.url);
    }
    imageCache.delete(filename);
    forceUpdate({});
  }, []);

  return {
    getCachedImage,
    fetchAndCacheImage,
    clearCache,
    removeCachedImage,
    hasCachedImage: useCallback((filename) => imageCache.has(filename), [])
  };
};

export default useImageCache;

